import React, { useState } from 'react';
import Markdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import type { Components } from 'react-markdown';

interface MessageProps {
  content: string;
  role: 'user' | 'assistant' | 'system';
  isCollapsed?: boolean;
}

interface CodeComponentProps {
  node?: any;
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
}

interface MathComponentProps {
  value: string;
}

interface CustomComponents extends Components {
  math: React.ComponentType<MathComponentProps>;
  inlineMath: React.ComponentType<MathComponentProps>;
}

const Message: React.FC<MessageProps> = ({ content, role, isCollapsed = false }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = (code: string, language: string) => {
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const extension = language === 'python' ? 'py' : language;
    a.download = `rhea.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const components: CustomComponents = {
    code({ node, inline, className, children, ...props }: CodeComponentProps) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      
      if (!inline && match) {
        return (
          <div className="relative group">
            <div className="absolute right-2 top-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => handleCopy(String(children))}
                className="px-2 py-1 text-xs bg-gray-700 rounded hover:bg-gray-600"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={() => handleDownload(String(children), language)}
                className="px-2 py-1 text-xs bg-gray-700 rounded hover:bg-gray-600"
              >
                Download
              </button>
            </div>
            <SyntaxHighlighter
              style={vscDarkPlus as any}
              language={language}
              PreTag="div"
              {...props}
            >
              {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
          </div>
        );
      }
      return <code className={className} {...props}>{children}</code>;
    },
    math: ({ value }: MathComponentProps) => (
      <span className="math math-display">
        {value}
      </span>
    ),
    inlineMath: ({ value }: MathComponentProps) => (
      <span className="math math-inline">
        {value}
      </span>
    )
  };

  return (
    <div
      className={`flex items-start gap-2 ${
        role === 'user' ? 'justify-end' : ''
      }`}
    >
      {role === 'assistant' && (
        <img src="/assets/rhea/logo.png" alt="Rhea" className="h-6 mt-1" />
      )}
      <div
        className={`p-3 rounded-lg ${
          isCollapsed ? 'max-h-12 overflow-hidden' : 'max-w-[80%]'
        }`}
        style={{ 
          backgroundColor: role === 'user' 
            ? 'var(--rhea-message-user)'
            : role === 'system'
            ? 'var(--rhea-message-system)'
            : 'var(--rhea-message-system)',
          color: 'var(--rhea-text-primary)'
        }}
      >
        <Markdown
          remarkPlugins={[remarkMath]}
          rehypePlugins={[rehypeKatex]}
          components={components}
        >
          {content}
        </Markdown>
      </div>
    </div>
  );
};

export default Message; 